// src/components/InAppNotification.js
// Premium glassmorphism in-app notification popup
// Shown on Dashboard when there's an unseen announcement from Danielle
// Any dismiss method (X, CTA, backdrop) = permanent dismissal
//
// The CARD is exported on its own (InAppNotificationCard) so the admin editor
// previews the real thing — before 2026-10-06 the editor drew a separate
// white mock-up that had drifted from what members actually saw.
//
// Optional image (imageUrl + imageSize) sits above the title, shown whole and
// never cropped, through the SAME CoachPostImage component Coach Posts use.
// The button lives in a footer OUTSIDE the scrolling area, so a tall image on a
// phone can never push it off-screen; a soft fade appears only when the
// content actually scrolls. With no image and short text the layout is
// identical to before.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormattedText from './FormattedText';
import CoachPostImage from './CoachPostImage';

const POPUP_Z = 10000;
const CTA_SHADOW = '0 4px 16px color-mix(in srgb, var(--mom-primary) 30%, transparent), 0 1px 3px rgba(0,0,0,0.1)';
const CTA_SHADOW_HOVER = '0 6px 20px color-mix(in srgb, var(--mom-primary) 35%, transparent), 0 2px 6px rgba(0,0,0,0.12)';

export function InAppNotificationCard({ notification, onClose, onCTA, closing = false, preview = false, maxHeight = '85vh' }) {
    const scrollRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);
    const [closeHover, setCloseHover] = useState(false);
    const [ctaHover, setCtaHover] = useState(false);

    const imageUrl = (notification?.imageUrl || '').trim();

    // Fade only when the content really scrolls (re-checked on resize and when
    // the image finishes loading, since that's what changes the height).
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
        check();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(check);
            ro.observe(el);
            if (el.firstElementChild) ro.observe(el.firstElementChild);
        }
        el.addEventListener('load', check, true);
        return () => { ro?.disconnect(); el.removeEventListener('load', check, true); };
    }, [imageUrl, notification?.body, notification?.title, maxHeight]);

    if (!notification) return null;
    const ctaLabel = notification.ctaLabel || 'Got it!';

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'relative',
                background: 'rgba(255, 230, 235, 0.6)',
                backdropFilter: 'blur(24px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                borderRadius: 28,
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 32px 80px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                maxWidth: 440,
                width: '100%',
                maxHeight,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: preview ? 'none' : (closing
                    ? 'inAppCardOut 0.25s ease-in forwards'
                    : 'inAppCardIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards'),
            }}
        >
            {/* Top edge highlight — glass reflection */}
            <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: 1,
                background: 'linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.5) 50%, transparent 90%)',
                pointerEvents: 'none',
                zIndex: 2,
                borderRadius: '28px 28px 0 0',
            }} />

            {/* Close button — always accessible, 44px hit target */}
            <button
                onClick={onClose}
                aria-label="Close notification"
                onMouseEnter={() => setCloseHover(true)}
                onMouseLeave={() => setCloseHover(false)}
                style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 3,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    background: closeHover ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.15s ease',
                    padding: 0,
                    margin: -4,
                    boxSizing: 'content-box',
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mom-charcoal)" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>

            {/* Scrolling content */}
            <div style={{ position: 'relative', zIndex: 1, flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <div
                    ref={scrollRef}
                    style={{
                        padding: '36px 32px 0',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        flex: '1 1 auto',
                        minHeight: 0,
                    }}
                >
                    <div>
                        {/* Image — above the title, shown whole. Spacing rides on the
                            image itself so a broken link collapses to nothing. */}
                        {imageUrl && (
                            <div style={{ textAlign: 'center' }}>
                                <CoachPostImage
                                    src={imageUrl}
                                    size={notification.imageSize}
                                    lightboxZIndex={POPUP_Z + 50}
                                    style={{ marginTop: 18, marginBottom: 20 }}
                                />
                            </div>
                        )}

                        {/* Title — charcoal text, legible on rose frosted glass */}
                        <h2 style={{
                            fontFamily: 'var(--mom-font-serif)',
                            fontSize: 'clamp(22px, 4vw, 26px)',
                            fontStyle: 'italic',
                            fontWeight: 700,
                            color: 'var(--mom-charcoal)',
                            lineHeight: 1.25,
                            marginBottom: 14,
                            paddingRight: imageUrl ? 0 : 32,
                        }}>
                            {notification.title}
                        </h2>

                        {/* Body — rendered through FormattedText */}
                        {/* marginBottom (not padding) so a trailing list's own margin
                            collapses into it exactly as in the original layout. */}
                        <div style={{ marginBottom: 28 }}>
                            <FormattedText
                                text={notification.body}
                                style={{
                                    fontFamily: 'var(--mom-font-sans)',
                                    fontSize: 15,
                                    color: 'var(--mom-charcoal)',
                                    lineHeight: 1.7,
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* "More below" fade — only while the content scrolls */}
                <div aria-hidden="true" style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, height: 36,
                    pointerEvents: 'none',
                    background: 'linear-gradient(to bottom, rgba(255, 236, 240, 0), rgba(255, 236, 240, 0.92))',
                    opacity: overflowing ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                }} />
            </div>

            {/* Footer — the button never scrolls away */}
            <div style={{ position: 'relative', zIndex: 1, flexShrink: 0, padding: '0 32px 32px' }}>
                <button
                    onClick={onCTA}
                    onMouseEnter={() => setCtaHover(true)}
                    onMouseLeave={() => setCtaHover(false)}
                    style={{
                        width: '100%',
                        padding: '15px 28px',
                        borderRadius: 50,
                        border: 'none',
                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                        color: 'white',
                        fontFamily: 'var(--mom-font-sans)',
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: ctaHover ? CTA_SHADOW_HOVER : CTA_SHADOW,
                        transform: ctaHover ? 'translateY(-1px)' : 'none',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        letterSpacing: '0.01em',
                    }}
                >
                    {ctaLabel}
                </button>
            </div>
        </div>
    );
}

function InAppNotification({ notification, onDismiss }) {
    const navigate = useNavigate();
    const [closing, setClosing] = useState(false);

    if (!notification) return null;

    const handleDismiss = () => {
        setClosing(true);
        // Brief exit animation before removing from DOM
        setTimeout(() => {
            if (onDismiss) onDismiss(notification.id);
        }, 250);
    };

    const handleCTA = () => {
        const link = notification.ctaLink;
        if (link) {
            // Dismiss first, then navigate
            if (onDismiss) onDismiss(notification.id);
            if (link.startsWith('http')) {
                window.open(link, '_blank', 'noopener,noreferrer');
            } else {
                navigate(link);
            }
        } else {
            handleDismiss();
        }
    };

    return (
        <div
            onClick={handleDismiss}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: POPUP_Z,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                animation: closing
                    ? 'inAppFadeOut 0.25s ease-out forwards'
                    : 'inAppBackdropIn 0.3s ease-out forwards',
            }}
        >
            {/* Light frosted backdrop — warm tint, lets dashboard show through softly */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(250, 245, 241, 0.65)',
                backdropFilter: 'blur(80px) saturate(1.5)',
                WebkitBackdropFilter: 'blur(80px) saturate(1.5)',
            }} />

            <InAppNotificationCard
                notification={notification}
                onClose={handleDismiss}
                onCTA={handleCTA}
                closing={closing}
            />

            {/* Keyframe animations — injected once */}
            <style>{`
                @keyframes inAppBackdropIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes inAppCardIn {
                    from {
                        opacity: 0;
                        transform: scale(0.92) translateY(16px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                @keyframes inAppCardOut {
                    from {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                    to {
                        opacity: 0;
                        transform: scale(0.95) translateY(8px);
                    }
                }
                @keyframes inAppFadeOut {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `}</style>
        </div>
    );
}

export default InAppNotification;
